import { useMemo, useState, useEffect } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Typography,
	Collapse,
	Box,
	Chip,
	Tooltip,
	Skeleton,
	CircularProgress
} from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import {
	useGetDocumentsListQuery,
	useDeleteDocumentMutation,
	useRetryDocumentMutation
} from '@/store/services/documents';
import { useGetAddressesListQuery } from '@/store/services/address';
import { toast } from 'react-toastify';
import ConfirmationDialog from "@/pages/apps/users/Dialog/ConfirmationDialog";

type Document = {
	id: number;
	file_name: string;
	file_type: string;
	document_file_url: string;
	is_download_doc?: boolean;
	is_enabled: number;
	processing_status?: string;
	address: {
		id: number;
		name: string;
		location_name: string;
		type: string;
	} | null;
};

type Address = {
	id: number;
	name: string | null;
	location_name: string | null;
	type: string | null;
	is_enabled: number;
};

function DocumentsTable() {
	const { data: documentsResponse, isLoading, refetch } = useGetDocumentsListQuery({});
	const { data: addressesResponse } = useGetAddressesListQuery({});
	const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
	const [retryDocument] = useRetryDocumentMutation();
	const [openRows, setOpenRows] = useState<Record<number, boolean>>({});
	const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);
	const [retryDocumentId, setRetryDocumentId] = useState<number | null>(null);
	const [retryDocumentStatus, setRetryDocumentStatus] = useState<string>('');
	const [isRetrying, setIsRetrying] = useState(false);

	const documents = documentsResponse?.data || [];
	const addresses = addressesResponse?.data || [];
	const shouldPollDocumentsList = useMemo(() => {
		return documents.some((doc: Document) => {
			const status = String(doc.processing_status || '').trim().toLowerCase();
			return status === 'pending' || status === 'extracting' || status === 'embedded';
		});
	}, [documents]);

	// Group documents by address
	const groupedDocuments = useMemo(() => {
		const grouped: Record<number, Document[]> = {};
		const ungrouped: Document[] = [];

		documents.forEach((doc: Document) => {
			if (doc.address?.id) {
				if (!grouped[doc.address.id]) {
					grouped[doc.address.id] = [];
				}

				grouped[doc.address.id].push(doc);
			} else {
				ungrouped.push(doc);
			}
		});

		return { grouped, ungrouped };
	}, [documents]);

	// Initialize openRows with all address IDs set to true (open by default)
	useEffect(() => {
		const initialOpenRows: Record<number, boolean> = {};
		Object.keys(groupedDocuments.grouped).forEach((addressId) => {
			initialOpenRows[Number(addressId)] = true;
		});
		setOpenRows((prev) => {
			// Only update if there are new addresses not in prev
			const hasNewAddresses = Object.keys(initialOpenRows).some((id) => prev[Number(id)] === undefined);

			if (hasNewAddresses) {
				return { ...prev, ...initialOpenRows };
			}

			return prev;
		});
	}, [groupedDocuments.grouped]);

	const handleToggleRow = (addressId: number) => {
		setOpenRows((prev) => ({
			...prev,
			[addressId]: !prev[addressId]
		}));
	};

	const handleDelete = (documentId: number) => {
		setSelectedDocumentId(documentId);
	};

	const handleDeleteConfirmation = async () => {
		if (!selectedDocumentId) return;

		try {
			const response = await deleteDocument({ documentId: selectedDocumentId });

			if (!response.error) {
				toast.success('Document deleted successfully');
			} else {
				toast.error(
					(response.error as { data?: { errors?: { detail?: string }[] } })?.data?.errors?.[0]?.detail ||
						'Failed to delete document'
				);
			}
		} catch (error) {
			toast.error('An error occurred while deleting the document');
			console.error(error);
		} finally {
			setSelectedDocumentId(null);
		}
	};

	const getAddressName = (addressId: number) => {
		const address = addresses.find((addr: Address) => addr.id === addressId);
		return address ? `${address.name || 'N/A'} - ${address.location_name || 'N/A'}` : 'Unknown Address';
	};

	const getFileTypeColor = (fileType: string) => {
		const typeMap: Record<string, 'primary' | 'success' | 'warning' | 'error'> = {
			pdf: 'error',
			png: 'primary',
			jpg: 'success',
			jpeg: 'success',
			doc: 'warning',
			docx: 'warning',
			txt: 'primary',
			csv: 'success',
			json: 'warning',
			webp: 'primary',
			xls: 'warning',
			xlsx: 'warning',
			ppt: 'error',
			pptx: 'error',
			svg: 'primary'
		};
		return typeMap[fileType.toLowerCase()] || 'default';
	};

	const getProcessingStatusValue = (status?: string) =>
		String(status || '').trim().toLowerCase() || 'unknown';

	const getProcessingStatusColor = (
		status?: string
	): 'success' | 'warning' | 'error' | 'default' => {
		const value = getProcessingStatusValue(status);
		if (value === 'completed') return 'success';
		if (['pending', 'extracting', 'embedding', 'embedded'].includes(value)) {
			return 'warning';
		}
		if (['failed', 'chunked', 'extracted'].includes(value)) return 'error';
		return 'default';
	};

	const getProcessingStatusLabel = (status?: string) => {
		const value = getProcessingStatusValue(status);
		if (value === 'completed') return 'Completed';
		if (value === 'pending') return 'Pending';
		if (value === 'extracting') return 'Extracting';
		if (['extracted', 'chunked', 'failed'].includes(value)) return 'Failed';
		if (value === 'embedding') return 'Embedding';
		if (value === 'embedded') return 'Embedded';
		return 'Unknown';
	};

	const canRetryStatus = (status?: string) => {
		const value = getProcessingStatusValue(status);
		return ['chunked', 'extracted', 'failed'].includes(value);
	};

	const handleRetryOpen = (documentId: number, status?: string) => {
		setRetryDocumentId(documentId);
		setRetryDocumentStatus(getProcessingStatusLabel(status));
	};

	const handleRetryConfirm = async () => {
		if (!retryDocumentId) return;
		try {
			setIsRetrying(true);
			const response = await retryDocument({ documentId: retryDocumentId });
			if (!response.error) {
				toast.success('Retry initiated successfully.');
			} else {
				toast.error(
					(response.error as { data?: { errors?: { detail?: string }[] } })?.data?.errors?.[0]
						?.detail || 'Failed to retry document processing.'
				);
			}
		} catch {
			toast.error('Failed to retry document processing.');
		} finally {
			setIsRetrying(false);
			setRetryDocumentId(null);
			setRetryDocumentStatus('');
		}
	};

	// Poll every 10 seconds while any document is pending or extracting (`/document`)
	useEffect(() => {
		if (!shouldPollDocumentsList) {
			return;
		}

		const intervalId = window.setInterval(() => {
			refetch();
		}, 10000);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [shouldPollDocumentsList, refetch]);

	// Keep skeleton only for initial load (avoid flicker during background polling)
	const showSkeleton = isLoading;

	return (
		<>
			<ConfirmationDialog
				open={!!selectedDocumentId}
				onClose={() => setSelectedDocumentId(null)}
				onConfirm={handleDeleteConfirmation}
				title="Delete Document"
				message="Are you sure you want to delete this document?"
				isLoading={isDeleting}
			/>
			<ConfirmationDialog
				open={!!retryDocumentId}
				onClose={() => {
					if (isRetrying) return;
					setRetryDocumentId(null);
					setRetryDocumentStatus('');
				}}
				onConfirm={handleRetryConfirm}
				title="Retry Document Status Processing"
				message={`Retry processing for this ${retryDocumentStatus || 'selected'} document status?`}
				isLoading={isRetrying}
			/>
			<Paper
				className="flex flex-col flex-auto pt-1 px-1 shadow-1 rounded-lg overflow-hidden mb-4"
				elevation={0}
			>
				<TableContainer>
					<Table stickyHeader className="min-w-[700px]">
						<TableHead>
							<TableRow>
								<TableCell
									colSpan={2}
									sx={{ paddingLeft: 6.4 }}
									width="30%"
								>
									<Typography className="font-semibold">Address</Typography>
								</TableCell>
								<TableCell width="40%">
									<Typography className="font-semibold">File Name</Typography>
								</TableCell>
								<TableCell width="15%">
									<Typography className="font-semibold">File Type</Typography>
								</TableCell>
								<TableCell width="15%">
									<Typography className="font-semibold">Status/Retry</Typography>
								</TableCell>
								<TableCell align="right" width="15%">
									<Typography className="font-semibold">Actions</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{showSkeleton ? (
								Array.from({ length: 6 }).map((_, i) => (
									<TableRow key={i}>
										<TableCell colSpan={2}>
											<Skeleton variant="rounded" width={120} height={24} animation="wave" />
										</TableCell>
										<TableCell>
											<Skeleton variant="rounded" width={180} height={24} animation="wave" />
										</TableCell>
										<TableCell>
											<Skeleton variant="rounded" width={60} height={24} animation="wave" />
										</TableCell>
										<TableCell>
											<Skeleton variant="rounded" width={90} height={24} animation="wave" />
										</TableCell>
										<TableCell align="right">
											<Skeleton variant="rounded" width={80} height={24} animation="wave" />
										</TableCell>
									</TableRow>
								))
							) : (
								<>
							{/* Grouped documents by address */}
							{Object.entries(groupedDocuments.grouped).map(([addressId, docs]) => {
								const addressIdNum = Number(addressId);
								const isOpen = openRows[addressIdNum] || false;
								const addressData = addresses.find((addr: Address) => addr.id === addressIdNum);
								const isAddressDisabled = addressData?.is_enabled === 0;

								return (
									<>
										<Tooltip
											title={
												isAddressDisabled
													? 'This address is disabled. Related documents cannot be modified.'
													: ''
											}
											arrow
											placement="top"
										>
											<TableRow
												key={`address-${addressIdNum}`}
												hover
												onClick={() => handleToggleRow(addressIdNum)}
												sx={{
													cursor: isAddressDisabled ? 'not-allowed' : 'pointer',
													opacity: isAddressDisabled ? 0.6 : 1
												}}
											>
												<TableCell colSpan={6}>
													<Box className="flex items-center gap-4">
														<FuseSvgIcon className="text-gray-500 text-sm">
															{isOpen
																? 'heroicons-solid:chevron-down'
																: 'heroicons-solid:chevron-right'}
														</FuseSvgIcon>
														<Box>
															<Typography className="font-medium">
																{getAddressName(addressIdNum)}
															</Typography>
															<Typography
																variant="body2"
																className="text-gray-500"
															>
																{docs.length} document{docs.length !== 1 ? 's' : ''}
															</Typography>
														</Box>
													</Box>
												</TableCell>
											</TableRow>
										</Tooltip>
										<TableRow>
											<TableCell
												style={{
													paddingBottom: 0,
													paddingTop: 0,
													paddingLeft: 0,
													paddingRight: 0
												}}
												colSpan={6}
											>
												<Collapse
													in={isOpen}
													timeout="auto"
													unmountOnExit
												>
													<Box sx={{ maxHeight: '220px', overflowY: 'auto' }}>
														<Table
															size="small"
															aria-label="documents"
														>
															<TableBody>
																{docs.map((doc, index) => {
																	const isDocDisabled =
																		doc.is_enabled === 0 || isAddressDisabled;
																	const canViewDocument = doc.is_download_doc === true;
																	const rowTooltipTitle = isDocDisabled
																		? 'This document is disabled because its address is disabled.'
																		: '';

																	return (
																	<Tooltip
																		key={index}
																		title={rowTooltipTitle}
																		arrow
																		placement="top"
																	>
																	<TableRow
																		sx={{
																			opacity: isDocDisabled ? 0.6 : 1,
																			cursor: isDocDisabled ? 'not-allowed' : 'default'
																		}}
																	>
																		<TableCell width="30%" />
																		<TableCell
																			colSpan={2}
																			width="40%"
																		>
																			<Tooltip title={doc.file_name} arrow placement="top"><Typography className="truncate max-w-xs">{doc.file_name}</Typography></Tooltip>
																		</TableCell>
																		<TableCell width="15%">
																			<Chip
																				label={doc.file_type.toUpperCase()}
																				color={getFileTypeColor(doc.file_type)}
																				size="small"
																			/>
																		</TableCell>
																		<TableCell width="15%">
																			<Box className="flex items-center gap-1">
																				<Chip
																					label={getProcessingStatusLabel(doc.processing_status)}
																					color={getProcessingStatusColor(doc.processing_status)}
																					size="small"
																					variant="outlined"
																				/>
																				{canRetryStatus(doc.processing_status) && (
																					<Tooltip title="Retry Processing" arrow placement="top">
																						<span>
																							<IconButton
																								size="small"
																								color="warning"
																								disabled={isDocDisabled || isRetrying}
																								onClick={() => {
																									if (isDocDisabled) return;
																								handleRetryOpen(doc.id, doc.processing_status);
																								}}
																							>
																								{isRetrying && retryDocumentId === doc.id ? (
																									<CircularProgress size={14} />
																								) : (
																									<FuseSvgIcon>
																										heroicons-outline:arrow-path
																									</FuseSvgIcon>
																								)}
																							</IconButton>
																						</span>
																					</Tooltip>
																				)}
																			</Box>
																		</TableCell>
																		<TableCell
																			align="right"
																			width="15%"
																		>
																			<Box className="flex justify-end gap-2">
																				{canViewDocument && (
																					<Tooltip
																						title={
																							rowTooltipTitle
																								? ''
																								: isDocDisabled
																									? 'Document is disabled'
																									: 'View Document'
																						}
																						arrow
																						placement="top"
																					>
																						<span>
																							<IconButton
																								size="small"
																								component={isDocDisabled ? 'button' : 'a'}
																								href={
																									isDocDisabled
																										? undefined
																										: doc.document_file_url
																								}
																								target={
																									isDocDisabled ? undefined : '_blank'
																								}
																								rel={
																									isDocDisabled
																										? undefined
																										: 'noopener noreferrer'
																								}
																								disabled={isDocDisabled}
																							>
																								<FuseSvgIcon>
																									heroicons-outline:eye
																								</FuseSvgIcon>
																							</IconButton>
																						</span>
																					</Tooltip>
																				)}
																				<Tooltip
																					title={
																						rowTooltipTitle
																							? ''
																							: isDocDisabled
																								? 'Document is disabled'
																								: 'Delete Document'
																					}
																					arrow
																					placement="top"
																				>
																					<span>
																						<IconButton
																							size="small"
																							color="error"
																							disabled={isDocDisabled}
																							onClick={() => {
																								if (isDocDisabled) return;
																								handleDelete(doc.id);
																							}}
																						>
																							<FuseSvgIcon>
																								heroicons-outline:trash
																							</FuseSvgIcon>
																						</IconButton>
																					</span>
																				</Tooltip>
																			</Box>
																		</TableCell>
																	</TableRow>
																	</Tooltip>
																)})}
															</TableBody>
														</Table>
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</>
								);
							})}

							{/* Ungrouped documents (no address) */}
							{groupedDocuments.ungrouped.map((doc, index) => {
								const isDocDisabled = doc.is_enabled === 0;
								const canViewDocument = doc.is_download_doc === true;
								const rowTooltipTitle = isDocDisabled ? 'This document is disabled.' : '';
								return (
								<Tooltip
									key={index}
									title={rowTooltipTitle}
									arrow
									placement="top"
								>
								<TableRow
									hover
									sx={{
										opacity: isDocDisabled ? 0.6 : 1,
										cursor: isDocDisabled ? 'not-allowed' : 'default'
									}}
								>
									<TableCell />
									<TableCell>
										<Typography className="text-gray-400 italic">No Address</Typography>
									</TableCell>
									<TableCell>
										<Typography>{doc.file_name}</Typography>
									</TableCell>
									<TableCell>
										<Chip
											label={doc.file_type.toUpperCase()}
											color={getFileTypeColor(doc.file_type)}
											size="small"
										/>
									</TableCell>
									<TableCell>
										<Box className="flex items-center gap-1">
											<Chip
												label={getProcessingStatusLabel(doc.processing_status)}
												color={getProcessingStatusColor(doc.processing_status)}
												size="small"
												variant="outlined"
											/>
											{canRetryStatus(doc.processing_status) && (
												<Tooltip title="Retry Processing" arrow placement="top">
													<span>
														<IconButton
															size="small"
															color="warning"
															disabled={isDocDisabled || isRetrying}
															onClick={() => {
																if (isDocDisabled) return;
																handleRetryOpen(doc.id, doc.processing_status);
															}}
														>
															{isRetrying && retryDocumentId === doc.id ? (
																<CircularProgress size={14} />
															) : (
																<FuseSvgIcon>heroicons-outline:arrow-path</FuseSvgIcon>
															)}
														</IconButton>
													</span>
												</Tooltip>
											)}
										</Box>
									</TableCell>
									<TableCell align="right">
										<Box className="flex justify-end gap-2">
											{canViewDocument && (
												<Tooltip
													title={
														rowTooltipTitle
															? ''
															: isDocDisabled
																? 'Document is disabled'
																: 'View Document'
													}
													arrow
													placement="top"
												>
													<span>
														<IconButton
															size="small"
															component={isDocDisabled ? 'button' : 'a'}
															href={
																isDocDisabled ? undefined : doc.document_file_url
															}
															target={isDocDisabled ? undefined : '_blank'}
															rel={
																isDocDisabled
																	? undefined
																	: 'noopener noreferrer'
															}
															disabled={isDocDisabled}
														>
															<FuseSvgIcon>heroicons-outline:eye</FuseSvgIcon>
														</IconButton>
													</span>
												</Tooltip>
											)}
											<Tooltip
												title={
													rowTooltipTitle
														? ''
														: isDocDisabled
															? 'Document is disabled'
															: 'Delete Document'
												}
												arrow
												placement="top"
											>
												<span>
													<IconButton
														size="small"
														color="error"
														disabled={isDocDisabled}
														onClick={() => {
															if (isDocDisabled) return;
															handleDelete(doc.id);
														}}
													>
														<FuseSvgIcon>heroicons-outline:trash</FuseSvgIcon>
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</TableCell>
								</TableRow>
								</Tooltip>
							)})}

							{/* Empty state */}
							{documents.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={6}
										align="center"
									>
										<Typography
											variant="body2"
											className="text-gray-500 py-8"
										>
											No documents found. Add your first document using the button above.
										</Typography>
									</TableCell>
								</TableRow>
							)}
							</>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</>
	);
}

export default DocumentsTable;
